// Hotspot manager — owns the 3D dot entities AND their HTML labels.
//
// Why both:
// • Desktop / mobile / Quest browser: HTML label is a labelled accessible
//   region projected onto the screen; pc.Picker resolves canvas taps to the
//   underlying 3D dot regardless of where the HTML label sits. Either click
//   path works (HTML click handler + canvas tap → picker).
// • In VR (immersive-vr): HTML overlays don't render. Controller rays use
//   pickFromRay (world-space) and the gaze reticle dwells on the same dots.
//
// Hotspot entities live on a dedicated pc.Layer ("hotspots"). The main
// camera is configured to render that layer (so dots are visible) AND
// the picker filters to that layer (so taps never accidentally hit the
// panorama sphere).

import * as pc from 'playcanvas';
import type { Hotspot } from '../app/types';
import { Emitter } from '../app/emitter';
import { yawPitchToVec3 } from './mathUtils';
import { HOTSPOT_DEFAULT_DISTANCE, HOTSPOT_BILLBOARD_RADIUS } from '../app/config';
import { HotspotPicker } from './picker';

export type HotspotSelectSource = 'click' | 'controller' | 'gaze';

export interface HotspotSelectEvent {
  hotspot: Hotspot;
  source: HotspotSelectSource;
}

/** Resolves a navigation hotspot's targetRoomId to a display name for the hover preview. */
export type RoomNameResolver = (roomId: string) => string | undefined;

interface SpawnedHotspot {
  hotspot: Hotspot;
  entity: pc.Entity;
  label: HTMLElement;
  worldPos: pc.Vec3;
  /** Last computed CSS-pixel screen position (label center); updated each tick. NaN when offscreen. */
  screenX: number;
  screenY: number;
}

const HOTSPOT_LAYER_NAME = 'hotspots';

export class HotspotManager {
  private readonly app: pc.Application;
  private readonly camera: pc.Entity;
  private readonly canvas: HTMLCanvasElement;
  private readonly container: HTMLElement;
  private readonly emitter = new Emitter<HotspotSelectEvent>();
  private readonly active: SpawnedHotspot[] = [];
  private readonly screenScratch = new pc.Vec3();
  private readonly typeMaterials = new Map<string, pc.StandardMaterial>();
  private readonly entityToHotspot = new Map<pc.Entity, Hotspot>();
  private readonly layer: pc.Layer;
  private readonly picker: HotspotPicker;
  // Hover-preview card — one shared DOM node, anchored above whichever nav
  // hotspot label currently has hover/focus. Positioned each tick.
  private readonly preview: HTMLElement;
  private readonly previewImg: HTMLImageElement;
  private readonly previewLabel: HTMLElement;
  private previewAnchor: HTMLElement | null = null;
  private resolveRoomName: RoomNameResolver | null = null;

  constructor(
    app: pc.Application,
    camera: pc.Entity,
    canvas: HTMLCanvasElement,
    container: HTMLElement,
    roomNameResolver?: RoomNameResolver,
  ) {
    this.app = app;
    this.camera = camera;
    this.canvas = canvas;
    this.container = container;
    this.resolveRoomName = roomNameResolver ?? null;

    this.preview = document.createElement('div');
    this.preview.className = 'hotspot-preview';
    this.preview.setAttribute('aria-hidden', 'true');
    this.previewImg = document.createElement('img');
    this.previewImg.alt = '';
    this.previewImg.decoding = 'async';
    this.previewImg.loading = 'lazy';
    this.previewLabel = document.createElement('div');
    this.previewLabel.className = 'hotspot-preview__label';
    this.preview.append(this.previewImg, this.previewLabel);
    this.container.appendChild(this.preview);

    // Create (or reuse) the dedicated hotspot layer and ensure the camera renders it.
    const existing = app.scene.layers.getLayerByName(HOTSPOT_LAYER_NAME);
    if (existing) {
      this.layer = existing;
    } else {
      this.layer = new pc.Layer({ name: HOTSPOT_LAYER_NAME });
      // Insert above WORLD so dots paint on top of the panorama.
      app.scene.layers.push(this.layer);
    }
    if (camera.camera) {
      const camLayers = camera.camera.layers ?? [];
      if (!camLayers.includes(this.layer.id)) {
        camera.camera.layers = [...camLayers, this.layer.id];
      }
    }

    this.picker = new HotspotPicker(app, camera, canvas, this.layer);
    this.app.on('update', this.tick);
  }

  setHotspots(hotspots: readonly Hotspot[]): void {
    this.clear();
    for (const hotspot of hotspots) this.spawn(hotspot);
    // First-frame project — otherwise labels flash at (0,0).
    this.tick(0);
  }

  on(listener: (e: HotspotSelectEvent) => void): () => void {
    return this.emitter.on(listener);
  }

  /**
   * Pixel-perfect canvas-tap pick via pc.Picker. Filtered to the hotspot layer,
   * so taps on empty wall (panorama) never resolve to a hotspot. Returns the
   * topmost hotspot under (clientX, clientY), or null.
   */
  pickFromScreen(clientX: number, clientY: number): Hotspot | null {
    const entity = this.picker.pickAtClient(clientX, clientY);
    if (!entity) return null;
    return this.entityToHotspot.get(entity) ?? null;
  }

  /**
   * VR controller / gaze pick: nearest hotspot to a world-space ray within
   * `tolerance` (radians of angular separation).
   */
  pickFromRay(rayOrigin: pc.Vec3, rayDir: pc.Vec3, toleranceRad = 0.07): Hotspot | null {
    let best: SpawnedHotspot | null = null;
    let bestAngle = toleranceRad;
    const v = new pc.Vec3();
    for (const sp of this.active) {
      v.sub2(sp.worldPos, rayOrigin).normalize();
      const dot = v.dot(rayDir);
      if (dot <= 0) continue;
      const angle = Math.acos(Math.min(1, Math.max(-1, dot)));
      if (angle < bestAngle) {
        bestAngle = angle;
        best = sp;
      }
    }
    return best?.hotspot ?? null;
  }

  triggerSelect(hotspot: Hotspot, source: HotspotSelectSource): void {
    this.emitter.emit({ hotspot, source });
  }

  destroy(): void {
    this.clear();
    this.preview.remove();
    this.picker.destroy();
    this.app.off('update', this.tick);
  }

  // ── Internals ─────────────────────────────────────────────────

  private clear(): void {
    if (this.previewAnchor) this.hidePreview(this.previewAnchor);
    for (const sp of this.active) {
      this.entityToHotspot.delete(sp.entity);
      sp.entity.destroy();
      sp.label.remove();
    }
    this.active.length = 0;
  }

  private spawn(hotspot: Hotspot): void {
    const distance = hotspot.distance ?? HOTSPOT_DEFAULT_DISTANCE;
    const pos = yawPitchToVec3(hotspot.yaw, hotspot.pitch, distance);

    const entity = new pc.Entity(`hotspot:${hotspot.id}`);
    entity.addComponent('render', {
      type: 'sphere',
      layers: [this.layer.id], // render only on the dedicated hotspot layer
    });
    entity.setLocalScale(HOTSPOT_BILLBOARD_RADIUS, HOTSPOT_BILLBOARD_RADIUS, HOTSPOT_BILLBOARD_RADIUS);
    const material = this.materialForType(hotspot.type);
    const meshInstances = entity.render?.meshInstances ?? [];
    for (const mi of meshInstances) mi.material = material;

    entity.setPosition(pos.x, pos.y, pos.z);

    this.app.root.addChild(entity);
    this.entityToHotspot.set(entity, hotspot);

    // Text-only HTML label, positioned below the 3D dot via worldToScreen
    // each frame. The colored ring/dot was retired in commit (this one) —
    // it was visually ambiguous against the 3D billboard dot in the scene
    // (users perceived "two indicators per hotspot, one anchored, one
    // glued to camera"). The 3D sphere alone communicates type via colour;
    // this label adds the human-readable name.
    // <div role="button"> instead of <button> — <button> elements have
    // built-in browser drag-handle behaviour (especially Safari and older
    // Chrome) where a slow mousedown on the focused button initiates a native
    // drag operation that the click event never recovers from. Divs carry no
    // such baggage; we re-add tab-focusability + ARIA + keyboard activation
    // by hand.
    const label = document.createElement('div');
    label.setAttribute('role', 'button');
    label.setAttribute('tabindex', '0');
    label.draggable = false;
    label.className = 'hotspot-label';
    label.dataset.hotspotId = hotspot.id;
    label.dataset.hotspotType = hotspot.type;
    label.setAttribute('aria-label', hotspot.label);
    label.textContent = hotspot.label;
    // Keep the click handler as defence-in-depth even though canvas-tap →
    // pc.Picker is the primary path now.
    const stop = (e: Event): void => e.stopPropagation();
    label.addEventListener('pointerdown', stop);
    label.addEventListener('pointerup', stop);
    label.addEventListener('touchstart', stop, { passive: true });
    label.addEventListener('mousedown', stop);
    // Block every drag-init event the browser might fire on a focused label.
    // dragstart preventDefault is the canonical kill-switch; the others catch
    // browser-specific quirks where the ghost has already started.
    const killDrag = (e: Event): void => {
      e.stopPropagation();
      e.preventDefault();
    };
    label.addEventListener('dragstart', killDrag);
    label.addEventListener('drag', killDrag);
    label.addEventListener('dragend', killDrag);
    label.addEventListener('click', (e) => {
      e.stopPropagation();
      this.triggerSelect(hotspot, 'click');
    });
    // Keyboard activation (Enter / Space) since we lost <button>'s native default.
    label.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.triggerSelect(hotspot, 'click');
      }
    });
    // Hover preview on navigation hotspots only — show a thumbnail of the
    // destination room above the label while the cursor or focus is on it.
    if (hotspot.type === 'navigation') {
      const targetRoomId = hotspot.targetRoomId;
      label.addEventListener('mouseenter', () => this.showPreview(label, targetRoomId));
      label.addEventListener('mouseleave', () => this.hidePreview(label));
      label.addEventListener('focusin', () => this.showPreview(label, targetRoomId));
      label.addEventListener('focusout', () => this.hidePreview(label));
    }
    this.container.appendChild(label);

    this.active.push({
      hotspot,
      entity,
      label,
      worldPos: new pc.Vec3(pos.x, pos.y, pos.z),
      screenX: NaN,
      screenY: NaN,
    });
  }

  private showPreview(anchor: HTMLElement, targetRoomId: string): void {
    const name = this.resolveRoomName?.(targetRoomId);
    if (!name) return;
    this.previewAnchor = anchor;
    this.previewImg.src = `/og/${encodeURIComponent(targetRoomId)}.jpg`;
    this.previewLabel.textContent = name;
    this.preview.classList.add('is-visible');
    this.preview.setAttribute('aria-hidden', 'false');
    this.positionPreview();
  }

  private hidePreview(anchor: HTMLElement): void {
    if (this.previewAnchor !== anchor) return;
    this.previewAnchor = null;
    this.preview.classList.remove('is-visible');
    this.preview.setAttribute('aria-hidden', 'true');
  }

  private positionPreview(): void {
    const anchor = this.previewAnchor;
    if (!anchor) return;
    if (anchor.classList.contains('is-far') || anchor.style.display === 'none') {
      this.hidePreview(anchor);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    this.preview.style.left = `${rect.left + rect.width / 2}px`;
    this.preview.style.top = `${rect.top - 12}px`;
  }

  private materialForType(type: string): pc.StandardMaterial {
    const cached = this.typeMaterials.get(type);
    if (cached) return cached;
    const mat = new pc.StandardMaterial();
    mat.useLighting = false;
    mat.useFog = false;
    mat.useTonemap = false;
    mat.useSkybox = false;
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.emissive = colorForType(type);
    mat.opacity = 0.9;
    mat.blendType = pc.BLEND_NORMAL;
    mat.depthWrite = false;
    mat.update();
    this.typeMaterials.set(type, mat);
    return mat;
  }

  private readonly tick = (_dt: number): void => {
    const cam = this.camera.camera;
    if (!cam) return;
    const cssW = this.canvas.clientWidth || window.innerWidth;
    const cssH = this.canvas.clientHeight || window.innerHeight;
    // Idle pulse on the per-type emissive materials so all hotspot dots breathe
    // subtly in sync. ~0.85..1.15 at 0.8 Hz reads as "alive" without being busy
    // — distinct from the louder portal pulse that was tried and rolled back.
    // Materials are shared across all hotspots of a given type, so this is one
    // write per type per frame regardless of hotspot count.
    const pulseT = performance.now() / 1000;
    const pulse = 1.0 + Math.sin(pulseT * 0.8 * Math.PI * 2) * 0.15;
    for (const mat of this.typeMaterials.values()) mat.emissiveIntensity = pulse;
    for (const sp of this.active) {
      cam.worldToScreen(sp.worldPos, this.screenScratch);
      const inFront = this.screenScratch.z > 0;
      if (!inFront) {
        sp.label.style.display = 'none';
        sp.screenX = NaN;
        sp.screenY = NaN;
        continue;
      }
      sp.label.style.display = '';
      // PlayCanvas 2.19's CameraComponent.worldToScreen returns CSS pixels
      // already (it passes device.clientRect into the underlying call). An
      // earlier /ratio division here was a no-op at DPR=1 but pushed labels
      // toward (0,0) on high-DPI / scaled displays — same regression that was
      // fixed in dollhouse.ts via 6f4b540. The cyan dots are 3D entities so
      // they sit at the correct world projection regardless; only the HTML
      // labels were drifting.
      const cssX = this.screenScratch.x;
      const cssY = this.screenScratch.y;
      sp.label.style.left = `${cssX}px`;
      // Offset the text label just below the 3D dot. Earlier value was 28 px
      // which felt detached on demo distances; 18 px pulls the label into the
      // dot's lower-edge area so the cyan circle and the caption read as one
      // marker. transform: translate(-50%, 0) keeps the label horizontally
      // centred on the dot.
      sp.label.style.top = `${cssY + 18}px`;
      sp.screenX = cssX;
      sp.screenY = cssY;
      const dxNorm = cssX / cssW - 0.5;
      const dyNorm = cssY / cssH - 0.5;
      const off = Math.hypot(dxNorm, dyNorm);
      sp.label.classList.toggle('is-far', off > 0.42);
    }
    // Reposition any active hover-preview anchor each frame so it tracks the
    // label during drag-look.
    if (this.previewAnchor) this.positionPreview();
  };
}

function colorForType(type: string): pc.Color {
  switch (type) {
    case 'navigation': return new pc.Color(0.31, 0.78, 1);
    case 'info':       return new pc.Color(1, 1, 1);
    case 'variant':    return new pc.Color(0.63, 0.47, 1);
    case 'video':      return new pc.Color(1, 0.39, 0.39);
    case 'cta':        return new pc.Color(0.96, 0.77, 0.19);
    case 'surface':    return new pc.Color(0.42, 0.92, 0.55); // mint green
    case 'note':       return new pc.Color(1, 0.85, 0.25);     // sticky-note yellow
    default:           return new pc.Color(1, 1, 1);
  }
}

